import { timezones } from "./utils/timezone";

interface CustomWindow extends Window {
  _env_: {
    APP_API: string;
    APP_API_V2: string;
    APP_MSP_API_V2: string;
    APP_MSP_API: string;
    APP_AUTH_API: string;
    APP_FIREBIRD_API: string;
    APP_LOGIN_REDIRECT_URL: string;
    APP_BASENAME: string;
    APP_DOMAIN: string;
  };
}

declare let window: CustomWindow;

export const API_URL = window._env_.APP_API;
export const V2_API_URL = window._env_.APP_API_V2;
export const V2_MSP_API_URL = window._env_.APP_MSP_API_V2;
export const MSP_API_URL = window._env_.APP_MSP_API;
export const AUTH_API_URL = window._env_.APP_AUTH_API;
export const FIREBIRD_API_URL = window._env_.APP_API + "/firebird";
export const LOGIN_REDIRECT_URL = window._env_.APP_LOGIN_REDIRECT_URL;
export const BASENAME = window._env_.APP_BASENAME;
export const IS_STORYBOOK = process.env.STORYBOOK_IS_ACTIVE;
export const DOMAIN = window._env_.APP_DOMAIN;

export const API_MSP_LIST_V2 = V2_API_URL + "/msps";
export const API_MSP_V2 = V2_API_URL + "/msps/:mspId";
export const API_SERVICE_PLAN_WORKLOAD_LIST = V2_MSP_API_URL + "/workloads";
export const API_MSP_SUSPEND = MSP_API_URL + "/msps/:mspId/suspend";
export const API_MSP_UNSUSPEND = MSP_API_URL + "/msps/:mspId/unsuspend";
export const API_MSP_PERMISSIONS = MSP_API_URL + "/msps/:mspId/permissions";
export const API_MSP_UPDATE_EMAIL =
  MSP_API_URL + "/msps/:mspId/admin/:adminId/update";
export const API_STORAGE_REGION_LIST = V2_API_URL + "/storage-regions";
export const API_FB_ACCOUNTS_LIST = FIREBIRD_API_URL + "/proxy_v3/accounts";
export const API_FB_AUDIT_TRAILS = FIREBIRD_API_URL + "/proxy_v3/audittrail";
export const API_FB_JOBS_LIST = FIREBIRD_API_URL + "/proxy_v3/jobs";
export const API_FB_ACCOUNT_DETAILS = FIREBIRD_API_URL + "/proxy_v2/v2/accounts/account/summary";
export const API_FB_RATE_CARDS_LIST = FIREBIRD_API_URL + "/proxy_v2/v2/factors";
export const API_FB_CONTRACT_UPDATE = FIREBIRD_API_URL + "/proxy_v3/accounts/:accountId/contracts/:contractId";
export const API_FB_CONTRACT_LINE_ITEM_UPDATE = FIREBIRD_API_URL + "/proxy_v3/contracts/:contractId/contractlineitems/:contractlineitemId";
export const API_SKU_LIST = API_URL + "/skus";
export const API_EDITION_LIST = API_URL + "/editions";
export const API_LOGOUT = API_URL + "/logout";
export const API_TASK = V2_MSP_API_URL + "/tasks/:taskId";
export const API_EDU_REQUESTS = V2_API_URL + "/msps/edurequests";
export const API_EDU_REQUESTS_UPDATE = V2_API_URL + "/msps/edurequests/:id";

export const API_USER_CURRENT = AUTH_API_URL + "/user-current";
export const API_REFRESH_TOKEN = AUTH_API_URL + "/token";

export const storageProviderNameToId = {
  aws: 1,
  azure: 2,
} as const;

export const genericSelectTypes = {
  storageRegions: "storageRegions",
  skus: "skus",
  editions: "editions",
  products: "products",
  datasources: "datasources",
};

export const dataSourceTypes = {
  M365: "Microsoft 365",
  "Enterprise Workloads": "Enterprise Workloads",
  Endpoints: "Endpoints",
  "Google Workspace": "Google Workspace",
  "Microsoft Entra ID": "Microsoft Entra ID",
  Okta: "Okta",
  "Microsoft Active Directory": "Microsoft Active Directory",
  Salesforce: "Salesforce",
  D365: "Dynamics 365",
};

/** Maps API/persisted datasource `name` to datasourceID (includes legacy names for existing MSPs). */
export const dataSourceTypesToID = {
  "Enterprise Workloads": 1,
  M365: 2,
  Endpoints: 3,
  "Google Workspace": 4,
  D365: 5,
  Salesforce: 6,
  "Microsoft Entra ID": 7,
  Okta: 8,
  "Microsoft Active Directory": 9,
};

/** Endpoints, D365, and Okta require Enterprise or Elite SKU (not Business). */
export const dataSourceIdsRequiringEnterpriseOrEliteSku = [3, 5, 8] as const;

/** Maps datasourceID to API `name` on create/update. */
export const dataSourceIdToDSName = {
  1: "Enterprise Workloads",
  2: "M365",
  3: "Endpoints",
  4: "Google Workspace",
  5: "D365",
  6: "Salesforce",
  7: "Microsoft Entra ID",
  8: "Okta",
  9: "Microsoft Active Directory",
};

export const productIDToName = {
  1: "Enterprise Workloads",
  2: "SaaS Apps and Endpoints",
};

export const dataSourceIdToName = {
  1: "Enterprise Workloads",
  2: "Microsoft 365",
  3: "Endpoints",
  4: "Google Workspace",
  5: "Dynamics 365",
  6: "Salesforce",
  7: "Microsoft Entra ID",
  8: "Okta",
  9: "Microsoft Active Directory",
};

export const PRODUCT_ID = {
  enterpriseWorkloads: 1,
  saasAppsAndEndpoints: 2,
};

export const MSP_FEATURE = {
  druInvestigate: "Dru Investigate",
} as const;

export const PRODUCT_FEATURE = {
  educationLicense: "Education License",
} as const;

export const CAM_JOBS_STATUS = {
  pending: 1,
  running: 2,
  paused: 3,
  finished: 4,
};

export const MSP_ACCOUNT_STATE = {
  pending: 0,
  ready: 1,
  markedForDeletion: 2,
  creationFailed: 3,
  suspended: 4,
};

export const MSP_BRAND_TYPE = {
  druva: 0,
  dell: 1,
};

export const brandTypeToName = {
  [MSP_BRAND_TYPE.druva]: "Druva",
  [MSP_BRAND_TYPE.dell]: "Dell",
};

export const toBrandTypeFormValue = (brandType?: number | string) =>
  String(brandType ?? MSP_BRAND_TYPE.druva);

export const toBrandTypeApiValue = (brandType: string | number) =>
  Number(brandType);

export const isDellBrandType = (brandType: string | number) =>
  toBrandTypeApiValue(brandType) === MSP_BRAND_TYPE.dell;

export const accountTypeOptions = [
  { label: "Druva", value: toBrandTypeFormValue(MSP_BRAND_TYPE.druva) },
  { label: "Dell", value: toBrandTypeFormValue(MSP_BRAND_TYPE.dell) },
];

export const TimezoneOptions = Object.keys(timezones)
  .sort((a, b) => a.localeCompare(b))
  .map((key) => ({
    label: key,
    value: key,
  }));

export const PAGE_SIZE = 50;

export const COLOR_WARNING = "#FF9900";
export const COLOR_DANGER = "#DC3545";

export const dateFormatter = new Intl.DateTimeFormat("en-US", {
  dateStyle: "medium",
});

export const minDate = new Date();
export const minYear = minDate.getFullYear() - 1;
export const maxYear = minYear + 8;

export const genericErrorMessage = "Unexpected error occured";

export const QUERIES = {
  getMspList: "get-msp-list-all-page",
  getEduRequestList: "get-edu-request-list",
  getStorageRegionsList: "get-storage-regions-list",
  getMSP: "msp",
  getFbAccountsList: "get-fb-accounts-list",
  getFbAuditTrailsList: "get-fb-audit-trails-list",
  getFbJobsList: "get-fb-jobs-list",
  getFbAccountDetails: "get-fb-account-details",
  getFbRateCardsList: "get-fb-rate-cards-list",
};

export enum FormKey {
  EDU_REQUEST_ACTION = "eduRequestAction",
}

export type SortOrder = "asc" | "desc";
